import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  Container, Box, Typography, Paper, Grid, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Chip, IconButton, InputAdornment, Fade, Card, CardContent,
  Avatar, Skeleton, Tooltip, MenuItem, Select, FormControl, InputLabel,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, IconButton as MuiIconButton
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Search, Person, Phone, LocationOn, School, HowToVote,
  Refresh, Visibility, Computer, PhoneIphone, Tablet, CalendarToday, Email,
  Lock, VisibilityOff, AdminPanelSettings, Logout, NavigateNext, NavigateBefore
} from "@mui/icons-material";

const theme = createTheme({
  palette: {
    primary: { main: "#1B75BC", light: "#4A90D9", dark: "#0D5AA7", contrastText: "#FFFFFF" },
    secondary: { main: "#ED1C24", light: "#F04A4F", dark: "#C4161C", contrastText: "#FFFFFF" },
    warning: { main: "#FDB813", light: "#FFC833", dark: "#E5A611", contrastText: "#1A202C" },
    background: { default: "#F8FAFC", paper: "#FFFFFF" },
    text: { primary: "#1A202C", secondary: "#4A5568" },
    success: { main: "#059669" },
    info: { main: "#3B82F6" },
  },
  typography: {
    fontFamily: "'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 16, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' } },
    },
    MuiTableCell: {
      styleOverrides: { root: { padding: '16px 24px', borderBottom: '1px solid #E2E8F0' } },
    },
    MuiTableHead: {
      styleOverrides: { root: { backgroundColor: '#F8FAFC', '& .MuiTableCell-root': { fontWeight: 600, color: '#1A202C', borderBottom: '2px solid #E2E8F0' } } },
    },
  },
});

const supabaseUrl = process.env.REACT_APP_SUPABASE_PROJECT_ID;
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLIC_ANON_KEY;
const supabase = createClient(supabaseUrl || '', supabaseKey || '');

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'asfcasper123';
const SESSION_KEY = 'admin_session';

interface YouthProfile {
  id: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  suffix_name: string | null;
  sex: string;
  birth_date: string;
  civil_status: string;
  email: string | null;
  contact_number: string;
  address: string;
  zone: string;
  youth_classification: string | null;
  highest_education: string;
  work_status: string;
  registered_voter: boolean | null;
  voted_last_election: boolean | null;
  attended_sk_assembly: boolean | null;
  assembly_attendance_count: number | null;
  device_info: any;
  created_at: string;
}

// Login Component
function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate network delay for security
    setTimeout(() => {
      if (password === ADMIN_PASSWORD) {
        sessionStorage.setItem(SESSION_KEY, 'authenticated');
        onLogin();
      } else {
        setError('Incorrect password. Please try again.');
        setIsLoading(false);
      }
    }, 500);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: '1px solid rgba(27, 117, 188, 0.1)',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1B75BC 0%, #F15A29 50%, #FDB813 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <AdminPanelSettings sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(45deg, #1B75BC 30%, #ED1C24 50%, #FDB813 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Admin Access
          </Typography>

          <Typography variant="body1" sx={{ color: '#4A5568', mb: 4 }}>
            Enter your password to access the Youth Profiling Dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#1B75BC' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <MuiIconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </MuiIconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading || !password}
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #1B75BC 30%, #0D5AA7 90%)',
                fontWeight: 'bold',
                borderRadius: 3,
                '&:hover': {
                  background: 'linear-gradient(45deg, #0D5AA7 30%, #1B75BC 90%)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: '#94A3B8',
                },
              }}
            >
              {isLoading ? 'Verifying...' : 'Access Dashboard'}
            </Button>
          </Box>

          <Typography variant="caption" sx={{ color: '#94A3B8', mt: 4, display: 'block' }}>
            Authorized personnel only
          </Typography>
        </Paper>
      </Fade>
    </Container>
  );
}

const getDeviceIcon = (deviceType: string) => {
  switch (deviceType?.toLowerCase()) {
    case 'mobile': return <PhoneIphone sx={{ fontSize: 16 }} />;
    case 'tablet': return <Tablet sx={{ fontSize: 16 }} />;
    default: return <Computer sx={{ fontSize: 16 }} />;
  }
};

const getZoneColor = (zone: string) => {
  const colors: Record<string, string> = {
    'Zone 1': '#1B75BC', 'Zone 2': '#F15A29', 'Zone 3A': '#FDB813', 'Zone 3B': '#F97316',
    'Zone 4': '#ED1C24', 'Zone 5': '#262262', 'Zone 6': '#059669',
    'Zone 7': '#7C3AED', 'Zone 8A': '#EC4899', 'Zone 8B': '#F59E0B',
  };
  return colors[zone] || '#64748B';
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const calculateAge = (birthDate: string) => {
  if (!birthDate) return 'N/A';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const DetailItem = ({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
    {icon && <Box sx={{ color: '#64748B', mt: 0.3 }}>{icon}</Box>}
    <Box>
      <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1A202C' }}>{value}</Typography>
    </Box>
  </Box>
);

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profiles, setProfiles] = useState<YouthProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedProfile, setSelectedProfile] = useState<YouthProfile | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Check for existing session
  useEffect(() => {
    const session = sessionStorage.getItem(SESSION_KEY);
    if (session === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
  };

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('youth_profiles')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (zoneFilter) query = query.eq('zone', zoneFilter);
      if (searchQuery) {
        query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,contact_number.ilike.%${searchQuery}%`);
      }

      const { data, error, count } = await query.range(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage - 1);

      if (error) throw error;
      setProfiles(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, zoneFilter, searchQuery, isAuthenticated]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getFullName = (profile: YouthProfile) => {
    let name = `${profile.first_name} ${profile.middle_name ? profile.middle_name + ' ' : ''}${profile.last_name}`;
    if (profile.suffix_name) name += ` ${profile.suffix_name}`;
    return name;
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <AdminLogin onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid rgba(27, 117, 188, 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><Person /></Avatar>
              <Box>
                <Typography variant="h4" sx={{ color: '#1A202C', fontWeight: 700, background: 'linear-gradient(45deg, #1B75BC 30%, #ED1C24 50%, #FDB813 70%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Youth Profiling Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#4A5568' }}>
                  View and search registered youth profiles
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Logout />}
              onClick={handleLogout}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Logout
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name or contact number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Zone</InputLabel>
                <Select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} label="Filter by Zone">
                  <MenuItem value="">All Zones</MenuItem>
                  {['Zone 1', 'Zone 2', 'Zone 3A', 'Zone 3B', 'Zone 4', 'Zone 5', 'Zone 6', 'Zone 7', 'Zone 8A', 'Zone 8B'].map((zone) => (
                    <MenuItem key={zone} value={zone}>{zone}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="outlined" onClick={fetchProfiles} startIcon={<Refresh />} sx={{ height: '100%', borderColor: '#E2E8F0', color: '#4A5568' }}>
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{totalCount}</Typography>
                  <Typography variant="body2">Total Registrations</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(27, 117, 188, 0.1)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Zone</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Education</TableCell>
                <TableCell>Work Status</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from(new Array(10)).map((_, i) => (<TableCell key={`cell-${i}`}><Skeleton /></TableCell>))}
                  </TableRow>
                ))
              ) : profiles.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><Typography color="textSecondary">No profiles found</Typography></TableCell></TableRow>
              ) : (
                profiles.map((profile) => (
                  <TableRow key={profile.id} hover sx={{ '&:hover': { bgcolor: 'rgba(27, 117, 188, 0.04)' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: getZoneColor(profile.zone), fontSize: '0.875rem' }}>
                          {profile.first_name[0]}{profile.last_name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{getFullName(profile)}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748B', textTransform: 'capitalize' }}>{profile.sex}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ fontSize: 16, color: '#64748B' }} />
                        <Typography variant="body2">{profile.contact_number}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={profile.zone} size="small" sx={{ bgcolor: `${getZoneColor(profile.zone)}15`, color: getZoneColor(profile.zone), fontWeight: 600, borderRadius: 1 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{calculateAge(profile.birth_date)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{profile.highest_education}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={profile.work_status} size="small" color={profile.work_status === 'Employed' ? 'success' : profile.work_status === 'Student' ? 'primary' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {profile.registered_voter === true ? (
                        <Chip icon={<HowToVote />} label="Yes" size="small" color="success" variant="outlined" />
                      ) : profile.registered_voter === false ? (
                        <Chip label="No" size="small" color="default" variant="outlined" />
                      ) : (
                        <Chip label="N/A" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(profile.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${profile.device_info?.browser || 'Unknown'} on ${profile.device_info?.deviceType || 'Desktop'}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
                          {getDeviceIcon(profile.device_info?.deviceType)}
                          <Typography variant="caption">{profile.device_info?.deviceType || 'Desktop'}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setSelectedProfile(profile)} sx={{ color: '#1B75BC' }}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination component="div" count={totalCount} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={[10, 25, 50, 100]} />
          {/* Custom pagination buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
              sx={{ minWidth: 40, borderRadius: 2 }}
            >
              <NavigateBefore />
            </Button>
            <Typography variant="body2" sx={{ mx: 1 }}>
              Page {page + 1} of {Math.max(1, Math.ceil(totalCount / rowsPerPage))}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPage((prev) => (prev + 1 < Math.ceil(totalCount / rowsPerPage) ? prev + 1 : prev))}
              disabled={page + 1 >= Math.ceil(totalCount / rowsPerPage)}
              sx={{ minWidth: 40, borderRadius: 2 }}
            >
              <NavigateNext /> 
            </Button>
          </Box>
        </TableContainer>

        <Dialog open={!!selectedProfile} onClose={() => setSelectedProfile(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          {selectedProfile && (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: getZoneColor(selectedProfile.zone), width: 56, height: 56 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>{selectedProfile.first_name[0]}{selectedProfile.last_name[0]}</Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{getFullName(selectedProfile)}</Typography>
                    <Typography variant="body2" color="textSecondary">ID: {selectedProfile.id.slice(0, 8)}... | Age: {calculateAge(selectedProfile.birth_date)} years old</Typography>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ color: '#1B75BC', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" /> Personal Information
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <DetailItem label="Sex" value={selectedProfile.sex} />
                      <DetailItem label="Civil Status" value={selectedProfile.civil_status} />
                      <DetailItem label="Birth Date" value={new Date(selectedProfile.birth_date).toLocaleDateString()} />
                      <DetailItem label="Youth Classification" value={selectedProfile.youth_classification || 'N/A'} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ color: '#FDB813', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" /> Contact Information
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <DetailItem label="Contact Number" value={selectedProfile.contact_number} icon={<Phone fontSize="small" />} />
                      <DetailItem label="Email" value={selectedProfile.email || 'N/A'} icon={<Email fontSize="small" />} />
                      <DetailItem label="Zone" value={selectedProfile.zone} icon={<LocationOn fontSize="small" />} />
                      <DetailItem label="Address" value={selectedProfile.address} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ color: '#ED1C24', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <School fontSize="small" /> Education & Work
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <DetailItem label="Highest Education" value={selectedProfile.highest_education} />
                      <DetailItem label="Work Status" value={selectedProfile.work_status} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ color: '#4A5568', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HowToVote fontSize="small" /> SK Participation
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <DetailItem label="Registered Voter" value={selectedProfile.registered_voter === true ? 'Yes' : selectedProfile.registered_voter === false ? 'No' : 'N/A'} />
                      <DetailItem label="Voted Last Election" value={selectedProfile.voted_last_election === true ? 'Yes' : selectedProfile.voted_last_election === false ? 'No' : 'N/A'} />
                      <DetailItem label="Attended SK Assembly" value={selectedProfile.attended_sk_assembly === true ? `Yes (${selectedProfile.assembly_attendance_count || 0} times)` : selectedProfile.attended_sk_assembly === false ? 'No' : 'N/A'} />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Computer fontSize="small" /> Submission Details
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <DetailItem label="Submitted On" value={formatDate(selectedProfile.created_at)} icon={<CalendarToday fontSize="small" />} />
                      <DetailItem label="Device Type" value={selectedProfile.device_info?.deviceType || 'Desktop'} icon={getDeviceIcon(selectedProfile.device_info?.deviceType)} />
                      <DetailItem label="Browser" value={selectedProfile.device_info?.browser || 'Unknown'} />
                      <DetailItem label="Screen Resolution" value={selectedProfile.device_info?.screenResolution || 'N/A'} />
                      <DetailItem label="Timezone" value={selectedProfile.device_info?.timezone || 'N/A'} />
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedProfile(null)} variant="contained" sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </ThemeProvider>
  );
}

export default AdminDashboard;
